import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ListChecks, ChevronRight, ChevronDown, Search } from "lucide-react";

export const SelectorSincronizacionDialog = ({ open, hijas, onClose, onContinue }) => {
  const [sel, setSel] = useState(() => new Set());
  const [expandidos, setExpandidos] = useState(() => new Set());
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) { setSel(new Set((hijas || []).map((h) => h.id))); setExpandidos(new Set()); setQ(""); }
  }, [open, hijas]);

  const grupos = useMemo(() => {
    const g = {};
    (hijas || []).forEach((h) => {
      const k = h.maestra_id || "sin";
      (g[k] = g[k] || { maestra_id: h.maestra_id, maestra_nombre: h.maestra_nombre || "Sin maestra", items: [] }).items.push(h);
    });
    return Object.values(g).sort((a, b) => a.maestra_nombre.localeCompare(b.maestra_nombre));
  }, [hijas]);

  const term = q.trim().toLowerCase();
  const gruposFiltrados = useMemo(() => {
    if (!term) return grupos;
    return grupos
      .map((g) => {
        const maestraMatch = g.maestra_nombre.toLowerCase().includes(term);
        const items = maestraMatch ? g.items : g.items.filter((h) => (h.nombre || "").toLowerCase().includes(term));
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
  }, [grupos, term]);

  const total = (hijas || []).length;
  const todos = total > 0 && sel.size === total;

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTodos = () => setSel(todos ? new Set() : new Set((hijas || []).map((h) => h.id)));
  const toggleGrupo = (g) => setSel((p) => {
    const n = new Set(p); const ids = g.items.map((i) => i.id);
    const all = ids.every((id) => n.has(id));
    ids.forEach((id) => (all ? n.delete(id) : n.add(id)));
    return n;
  });
  const toggleExpand = (k) => setExpandidos((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="selector-sincronizacion-dialog" className="bg-white max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ListChecks size={18} className="text-indigo-600" /> ¿Qué plantillas generadas querés revisar?
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Elegí las plantillas a sincronizar. Con todas marcadas, revisa la totalidad (como antes).</p>
        </DialogHeader>

        <div className="shrink-0 space-y-2 border-b border-slate-100 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="selector-sincro-buscar" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por nombre de maestra o modelo…" className="pl-8 h-9" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
            <Checkbox data-testid="selector-sincro-todos" checked={todos} onCheckedChange={toggleTodos} />
            Seleccionar todas ({total})
          </label>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 py-2 pr-1">
          {total === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No hay plantillas generadas.</p>}
          {total > 0 && gruposFiltrados.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Sin coincidencias para “{q}”.</p>}
          {gruposFiltrados.map((g) => {
            const key = g.maestra_id || "sin";
            const ids = g.items.map((i) => i.id);
            const grpAll = ids.every((id) => sel.has(id));
            const grpSome = ids.some((id) => sel.has(id));
            const abierto = expandidos.has(key) || !!term;
            const marcadas = ids.filter((id) => sel.has(id)).length;
            return (
              <div key={key} className="rounded-sm border border-slate-200">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <button type="button" data-testid={`selector-sincro-expand-${g.maestra_id}`} onClick={() => toggleExpand(key)}
                    className="text-slate-500 hover:text-slate-800">
                    {abierto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold flex-1 min-w-0">
                    <Checkbox data-testid={`selector-sincro-grupo-${g.maestra_id}`} checked={grpAll ? true : grpSome ? "indeterminate" : false}
                      onCheckedChange={() => toggleGrupo(g)} />
                    <span className="truncate">{g.maestra_nombre}</span>
                    <span className="text-xs font-normal text-slate-400 shrink-0">— seleccionar todas ({ids.length}){marcadas > 0 && marcadas < ids.length ? ` · ${marcadas} marcada(s)` : ""}</span>
                  </label>
                </div>
                {abierto && (
                  <div className="pl-9 pr-2 pb-1.5 space-y-0.5">
                    {g.items.map((h) => (
                      <label key={h.id} data-testid={`selector-sincro-item-${h.id}`}
                        className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                        <Checkbox checked={sel.has(h.id)} onCheckedChange={() => toggle(h.id)} />
                        <span className="truncate">{h.nombre}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="selector-sincro-continuar" onClick={() => onContinue([...sel])} disabled={sel.size === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Continuar ({sel.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
